/**
 * Day 98.5b (Pre-Launch Polish — concurrent-users banner) — pure helpers.
 *
 * The testable logic behind the banner: actor-name resolution (with the unnamed
 * fallback for pre-instrumentation NULL updated_by rows) and relative-age
 * phrasing, kept free of React/Supabase. The banner component
 * (ConcurrentEditBanner.tsx) is a thin shell over these + the Realtime
 * subscription. `now` is injected so the formatters are deterministic in tests.
 */

/** Fallback when updated_by is NULL (pre-instrumentation rows) or the profile
 *  name is missing/blank — actor identity is genuinely unrecorded. */
export function resolveActorName(fullName: string | null | undefined): string {
  const trimmed = (fullName ?? '').trim();
  return trimmed.length > 0 ? trimmed : 'Another user';
}

/** Compact relative age ("just now" / "5m" / "2h" / "3d" / a date) of `iso`
 *  measured against `now` (ms). Mirrors the TopBar/DATA_STANDARDS relative-time
 *  shape; `now` is a param so callers + tests are deterministic. */
export function relativeAge(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'just now';
  const diffMin = Math.floor((now - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return new Date(then).toLocaleDateString();
}

/** The banner sentence: "{name} updated this {age} ago — refresh to see latest"
 *  (or "… just now — …" for the sub-minute case). */
export function concurrentEditMessage(
  fullName: string | null | undefined,
  iso: string,
  now: number
): string {
  const name = resolveActorName(fullName);
  const age = relativeAge(iso, now);
  return age === 'just now'
    ? `${name} updated this just now — refresh to see latest`
    : `${name} updated this ${age} ago — refresh to see latest`;
}
